import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Camera, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchProfile,
  saveProfile,
  fetchSettings,
  updateSettings,
  uploadProfilePicture,
  formatApiError,
} from "@/api/userAccount";

const LANGUAGE_OPTIONS = [
  { label: "English (US)", value: "English (US)" },
  { label: "Japanese", value: "Japanese" },
] as const;

export default function ProfileSettingsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: ["profile"],
    queryFn: fetchProfile,
  });

  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [language, setLanguage] = useState("English (US)");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const p = profileQuery.data?.profile;
    if (p) {
      setFirstName(p.firstName ?? "");
      setLastName(p.lastName ?? "");
      setContactNumber(p.contactNumber ?? "");
    }
  }, [profileQuery.data]);

  useEffect(() => {
    const s = settingsQuery.data?.settings;
    if (s) {
      setLanguage(s.language || "English (US)");
    }
  }, [settingsQuery.data]);

  const saveProfileMutation = useMutation({
    mutationFn: saveProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Profile saved");
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const updateSettingsMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Language saved");
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const displayEmail = profileQuery.data?.profile?.user?.email ?? user?.email ?? "";
  const profileImage = profileQuery.data?.profile?.image;
  const initials = (firstName?.[0] || displayEmail?.[0] || "?").toUpperCase();

  const MAX_AVATAR_SIZE_BYTES = 2 * 1024 * 1024;

  const onAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      toast.error("Avatar is too large (max 2MB)");
      return;
    }

    void (async () => {
      setAvatarUploading(true);
      try {
        const uploaded = await uploadProfilePicture(file);
        saveProfileMutation.mutate({
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          contactNumber: contactNumber.trim() || undefined,
          image: uploaded.url,
        });
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setAvatarUploading(false);
      }
    })();
  };

  const onSaveProfile = () => {
    saveProfileMutation.mutate({
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      contactNumber: contactNumber.trim() || undefined,
    });
    updateSettingsMutation.mutate({ language });
  };

  const loading = profileQuery.isPending || settingsQuery.isPending;

  return (
    <DashboardLayout>
      <div className="dash-page max-w-3xl">
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">Profile Settings</h1>
            <p className="dash-page-subtitle">
              Manage your profile details and language. {"·"} {" "}
              <Link to="/password-change" className="text-primary hover:underline font-medium">
                Change password
              </Link>
            </p>
          </div>
        </div>

        <KCard className="p-5 sm:p-6 md:p-8 space-y-6">
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-20 rounded-2xl" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-6">
                <div className="relative">
                  {profileImage ? (
                    <div className="w-20 h-20 rounded-2xl bg-primary/10 overflow-hidden">
                      <img src={profileImage} alt="Profile picture" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold">
                      {initials}
                    </div>
                  )}

                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onAvatarFileChange}
                  />

                  <button
                    type="button"
                    className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-lg flex items-center justify-center text-primary-foreground opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Upload profile picture"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={avatarUploading || saveProfileMutation.isPending}
                  >
                    <Camera size={14} />
                  </button>
                </div>
                <div>
                  <p className="font-bold text-foreground text-lg">
                    {[firstName, lastName].filter(Boolean).join(" ") || "Your name"}
                  </p>
                  <p className="text-sm text-muted-foreground">{displayEmail}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Email</Label>
                  <Input value={displayEmail} type="email" disabled className="bg-muted/50" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    placeholder="Contact number"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="language">Language</Label>
                  <select
                    id="language"
                    className="h-10 w-full px-3 rounded-lg border border-border bg-background text-sm text-foreground"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    {LANGUAGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={onSaveProfile}
                  disabled={saveProfileMutation.isPending || updateSettingsMutation.isPending}
                >
                  <Save size={16} /> {(saveProfileMutation.isPending || updateSettingsMutation.isPending) ? "Saving..." : "Save profile"}
                </Button>
              </div>
            </>
          )}
        </KCard>
      </div>
    </DashboardLayout>
  );
}
